import { PageOptions } from '@graphcommerce/framer-next-pages'
import {
  ProductFiltersPro,
  ProductListDocument,
  extractUrlQuery,
  parseParams,
  FilterTypes,
  ProductListParams,
  getFilterTypes,
  ProductFiltersDocument,
  ProductListQuery,
  ProductFiltersQuery,
} from '@graphcommerce/magento-product'
import {
  ProductFiltersProAllFiltersChip
} from '../../components/PriceFiltersPro'
import {
  ProductListCount
} from '../../components/ProductListCount/ProductListCount'
import {
  GetStaticProps,
  LayoutTitle,
  LayoutHeader,
} from '@graphcommerce/next-ui'
import {
  CategorySearchDocument,
  CategorySearchQuery,
  CategorySearchResult,
  NoSearchResults,
  SearchDivider,
} from '@graphcommerce/magento-search'
import { SearchForm } from '../../components/Search/SearchForm'
import { PageMeta, StoreConfigDocument } from '@graphcommerce/magento-store'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
// import { Container, Hidden } from '@mui/material'
import Container from '@mui/material/Container'
import Hidden from '@mui/material/Hidden'
import { LayoutNavigation, LayoutNavigationProps, ProductListItems } from '../../components'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { DefaultPageDocument, DefaultPageQuery } from '../../graphql/DefaultPage.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import {useInView} from 'react-intersection-observer'
import {useEffect, useState} from 'react'
import {Box, CircularProgress} from '@mui/material'
import router from 'next/router'
import {LayoutCategoryControl} from '../../components/Layout/LayoutCategoryControl'
import {SubCategoryNavigation} from '../../components/Navigation/SubCategoryNavigation'

type SearchResultProps = DefaultPageQuery &
  ProductListQuery &
  ProductFiltersQuery &
  CategorySearchQuery & { filterTypes: FilterTypes; params: ProductListParams }
type RouteProps = { url: string[] }
export type GetPageStaticProps = GetStaticProps<
  LayoutNavigationProps,
  SearchResultProps,
  RouteProps
>

const getProducts = async (params: ProductListParams) => {
  const { locale } = router
  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const pageSize = (await conf).data.storeConfig?.grid_per_page  ?? params.pageSize
  params.pageSize = pageSize

  const products = await client.query({
    query: ProductListDocument,
    variables: {
      ...params
    },
  })
  return { ...(products).data }
}

function SearchResultPage(props: SearchResultProps) {
  const { products, categories, params, filters, filterTypes } = props
  const search = params.url.split('/')[1]
  const totalSearchResults = (categories?.items?.length ?? 0) + (products?.total_count ?? 0)
  const noSearchResults = search && (!products || (products.items && products?.items?.length <= 0))

  const [ref, inView] = useInView();
  const [items, setItems] = useState(products?.items)
  const [currentPage, setCurrentPage] = useState(1)
  const [isFetchingPage, setIsFetchingPage] = useState(false)
  const totalPages = products?.page_info?.total_pages ?? 1

  useEffect(() => {
    if (!products) {
      return
    }
    const resetPage = 1
    setCurrentPage(resetPage)
    setIsFetchingPage(false)
    setItems(products.items)
  }, [products])

  useEffect(() => {
    if (isFetchingPage || !inView || !params || currentPage > totalPages) {
      return
    }

    setIsFetchingPage(true)
    const {
      url,
      sort,
      filters: paramsFilters,
    }: ProductListParams = params
    const nextPage = currentPage + 1
    const newParams = { filters: paramsFilters, sort, url, currentPage: nextPage, pageSize: 12, search: search ?? '' } as ProductListParams
    getProducts(newParams).then((data) => {
      setItems(items?.concat(data?.products?.items as Array<any>))
      setCurrentPage(nextPage)
    }).catch((error) => {
      console.log("Error", error)
    }).finally(() => {
      setIsFetchingPage(false)
    })
  }, [inView])

  return (
    <>
      <PageMeta
        title={
          search
            ? i18n._(/* i18n */ 'Results for ‘{search}’', { search })
            : i18n._(/* i18n */ 'Search')
        }
        metaRobots={['noindex']}
        canonical='/search'
      />
      <LayoutHeader floatingMd switchPoint={0}>
        <LayoutTitle size='small'>
          <SearchForm
            totalResults={totalSearchResults}
            search={search}
            textFieldProps={{ variant: 'standard' }}
          />
        </LayoutTitle>
      </LayoutHeader>
      {products && products.items && products?.items?.length > 0 && (
        <LayoutCategoryControl>
          <Box sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'end',
            marginLeft: 'auto',
          })}>
            <ProductListCount total_count={products?.total_count} sx={(theme) => ({
              display: { xs: 'none', md: 'block' },
            })} />
            <ProductFiltersPro params={params}>
              <ProductFiltersProAllFiltersChip
                {...filters}
                appliedAggregations={products.aggregations}
                filterTypes={filterTypes}
              />
            </ProductFiltersPro>
          </Box>
        </LayoutCategoryControl>
      )}
      <Hidden implementation='css' mdDown>
        <LayoutTitle gutterBottom={false} gutterTop={false}>
          {search ? (
            <Trans id='Results for &lsquo;{search}&rsquo;' values={{ search }} />
          ) : (
            <Trans id='All products' />
          )}
        </LayoutTitle>

        <Container maxWidth='sm'>
          <SearchForm
            totalResults={totalSearchResults}
            search={search}
            textFieldProps={{ autoFocus: true }}
          />

          {categories?.items?.map((category) => (
            <CategorySearchResult key={category?.url_path} search={search} {...category} />
          ))}
        </Container>
        <SearchDivider />
      </Hidden>

      {noSearchResults && <NoSearchResults search={search} />}
      {products && products.items && products?.items?.length > 0 && (
        <Container maxWidth={false}>
          <ProductListItems title={`Search ${search}`} items={items} loadingEager={1} />
          <Box
            ref={ref}
            sx={(theme) => ({
              margin: '0 auto',
              marginTop: theme.spacings.lg,
              marginBottom: theme.spacings.lg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            })}
          >
            { isFetchingPage && <CircularProgress color='inherit' size={20} /> }
          </Box>
        </Container>
      )}
    </>
  )
}

const pageOptions: PageOptions<LayoutNavigationProps> = {
  Layout: LayoutNavigation,
}

SearchResultPage.pageOptions = pageOptions

export default SearchResultPage

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  const [searchShort = '', query = []] = extractUrlQuery(params)
  const search = searchShort.length >= 3 ? searchShort : ''

  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const filterTypes = getFilterTypes(client)

  const staticClient = graphqlSsrClient(locale)
  const page = staticClient.query({ query: DefaultPageDocument, variables: { url: 'search' } })
  const layout = staticClient.query({ query: LayoutDocument })

  const productListParams = parseParams(
    search ? `search/${search}` : 'search',
    query,
    await filterTypes,
    search,
  )

  if (!productListParams) return { notFound: true, revalidate: 60 * 20 }

  const filters = staticClient.query({ query: ProductFiltersDocument, variables: { search } })

  const products = staticClient.query({
    query: ProductListDocument,
    variables: { ...productListParams, search, pageSize: 12 },
  })

  const categories = search
    ? staticClient.query({ query: CategorySearchDocument, variables: { search } })
    : undefined

  return {
    props: {
      ...(await page).data,
      ...(await products).data,
      ...(await filters).data,
      ...(await categories)?.data,
      ...(await layout)?.data,
      filterTypes: await filterTypes,
      params: productListParams,
      up: { href: '/', title: 'Home' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 60 * 12, // 12 hours
  }
}
